// The explored map as the page draws it (MapChunks: one canvas per explored chunk, shared by the world map and the
// minimap) and the minimap in the top right corner: the zone's name and the time over the explored land around you,
// with you, your party, other players, NPCs and mobs nearby. Click it for the world map (M); the wheel zooms it.
import { api, content } from '../api';
import { Widget, widget } from '../ui/compose';
import type { Frame } from './types';

export interface MapData {
  reset: boolean; zone: string; x: number; y: number; tx: number; ty: number; f: number; explored: number; dirty: [number, number][];
  entrances: [number, number][]; party: { name: string; here: boolean; where: string; x: number; y: number }[];
}

const GROUND_COLORS: Record<string, [number, number, number]> = {
  DeepWater: [15, 90, 168], Water: [28, 122, 202], Sand: [230, 208, 152], Grass: [108, 176, 30], DryGrass: [176, 170, 68], Dirt: [212, 164, 82],
  Ash: [92, 86, 88], Cobble: [190, 178, 146], Stone: [142, 137, 132], Cliff: [122, 102, 80], CaveFloor: [74, 63, 70], CaveWall: [42, 34, 41], Lava: [232, 86, 28], WoodFloor: [168, 110, 58],
};
const FEATURE_COLORS: Record<number, [number, number, number]> = { 1: [38, 116, 40], 2: [100, 96, 92], 3: [74, 46, 30], 4: [12, 8, 10], 5: [250, 225, 160] };

/** Explored chunks as small canvases (a pixel per tile), kept up to date from the session's map memory. */
export class MapChunks {
  readonly chunks = new Map<string, HTMLCanvasElement>();
  /** What the last refresh said: where you are, cave entrances, your party. */
  last: MapData | null = null;
  private readonly groundColor: [number, number, number][] = [];

  constructor() {
    for (const [name, id] of Object.entries(content.named)) if (name.startsWith('ground') && GROUND_COLORS[name.slice(6)]) this.groundColor[id] = GROUND_COLORS[name.slice(6)];
  }

  /** Pulls the chunks explored or changed since the last refresh (a new zone starts over). */
  refresh(): MapData {
    const d = this.last = api.map() as MapData;
    if (d.reset) this.chunks.clear();
    for (const [x, y] of d.dirty) {
      const bytes = api.mapChunk(x, y);
      if (!bytes.length) continue;
      const c = document.createElement('canvas');
      c.width = c.height = 32;
      const g = c.getContext('2d')!, img = g.createImageData(32, 32);
      for (let i = 0; i < 1024; i++) {
        const code = bytes[i], feature = code >> 4;
        let col = FEATURE_COLORS[feature] ?? this.groundColor[code & 15] ?? [8, 6, 8];
        if (!FEATURE_COLORS[feature] && (i + (i >> 5)) % 2 === 1) col = col.map(v => Math.round(v * 0.94)) as [number, number, number];
        img.data.set([col[0], col[1], col[2], 255], i * 4);
      }
      g.putImageData(img, 0, 0);
      this.chunks.set(`${x},${y}`, c);
    }
    return d;
  }
}

/** Pixels per tile at each zoom step (times the UI scale). */
const ZOOMS = [1, 2, 3, 4];
/** The minimap's square, in UI pixels (times the UI scale). */
const SIZE = 104;

export class Minimap {
  readonly el: Widget;
  private readonly canvas = widget('raster', { class: 'sprite minimap-canvas' });
  private readonly zone = widget('text', { class: 'accent grow', style: { whiteSpace: 'nowrap' } });
  private readonly clock = widget('text', { class: 'dim small' });
  private zoom = 1;
  private lastDraw = -Infinity;

  constructor(private readonly map: MapChunks, onClick: () => void) {
    this.el = widget('box', { class: 'panel minimap' }, widget('box', { class: 'row minimap-header' }, this.zone, this.clock), this.canvas);
    this.el.title = 'Click: world map (M)   Wheel: zoom';
    this.canvas.addEventListener('mousedown', e => { e.stopPropagation(); if (e.button === 0) onClick(); });
    this.canvas.addEventListener('wheel', e => {
      e.preventDefault();
      e.stopPropagation();
      this.zoom = Math.max(0, Math.min(ZOOMS.length - 1, this.zoom + (e.deltaY < 0 ? 1 : -1)));
      this.lastDraw = -Infinity;
    }, { passive: false });
  }

  /** Every frame: the header, then the land around you and who's near (from the frame, so they move smoothly). */
  draw(f: Frame, clock: string): void {
    this.zone.textContent = f.zone.name + (f.zone.instanced ? `  #${f.zone.instance}` : '');
    this.clock.textContent = f.zone.dark ? '' : clock;
    // Canvas-to-GPU uploads need not run at the world's render rate.
    if (f.t >= this.lastDraw && f.t - this.lastDraw < 1 / 20) return;
    this.lastDraw = f.t;
    const size = SIZE, c = this.canvas;
    if (c.surfaceWidth !== size) { c.surfaceWidth = c.surfaceHeight = size; c.style.width = c.style.height = `${size}px`; }
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#0c080a';
    g.fillRect(0, 0, size, size);

    const ts = 16, scale = ZOOMS[this.zoom];
    const cx = f.me.x / ts, cy = f.me.y / ts;
    const toScreen = (tx: number, ty: number): [number, number] => [Math.floor((tx - cx) * scale + size / 2), Math.floor((ty - cy) * scale + size / 2)];
    const half = size / 2 / scale;
    for (let y = Math.floor((cy - half) / 32); y <= Math.floor((cy + half) / 32); y++)
      for (let x = Math.floor((cx - half) / 32); x <= Math.floor((cx + half) / 32); x++) {
        const chunk = this.map.chunks.get(`${x},${y}`);
        if (!chunk) continue;
        const [x0, y0] = toScreen(x * 32, y * 32), [x1, y1] = toScreen((x + 1) * 32, (y + 1) * 32);
        g.drawImage(chunk, x0, y0, x1 - x0, y1 - y0);
      }

    const dot = (tx: number, ty: number, color: string, r: number) => {
      const [px, py] = toScreen(tx, ty);
      if (px < -r || py < -r || px > size + r || py > size + r) return;
      g.fillStyle = '#000';
      g.fillRect(px - r - 1, py - r - 1, (r + 1) * 2, (r + 1) * 2);
      g.fillStyle = color;
      g.fillRect(px - r, py - r, r * 2, r * 2);
    };
    const small = Math.max(1, 1), big = Math.max(2, Math.round(1.5));
    for (const [x, y] of this.map.last?.entrances ?? []) dot(x + 0.5, y + 0.5, '#f9923c', big);
    for (const n of f.npcs) dot(n.x / ts, n.y / ts, '#beeb96', small);
    for (const e of f.ents) {
      if (e.mob) dot(e.x / ts, e.y / ts, '#e05a4a', small);
      else dot(e.x / ts, e.y / ts, e.party ? '#78d2f0' : '#f2ece4', big);
    }
    // Party members out of view range (sent a few times a second, any distance): at the edge if they're off the map.
    for (const p of this.map.last?.party ?? []) {
      if (!p.here || f.ents.some(e => !e.mob && e.name === p.name)) continue;
      const clampTo = (v: number, c0: number) => Math.max(c0 - half + 1, Math.min(c0 + half - 1, v));
      dot(clampTo(p.x, cx), clampTo(p.y, cy), '#78d2f0', big);
    }

    // You: an arrow showing your facing, in the middle.
    const m = size / 2, r = 4;
    const dir = [[0, 1], [-1, 0], [1, 0], [0, -1]][f.me.f] ?? [0, 1];
    g.fillStyle = '#ffd678'; g.strokeStyle = '#000'; g.lineWidth = 1;
    g.beginPath();
    g.moveTo(m + dir[0] * r, m + dir[1] * r);
    g.lineTo(m - dir[0] * r / 2 + dir[1] * r * 0.8, m - dir[1] * r / 2 + dir[0] * r * 0.8);
    g.lineTo(m - dir[0] * r / 2 - dir[1] * r * 0.8, m - dir[1] * r / 2 - dir[0] * r * 0.8);
    g.closePath(); g.fill(); g.stroke();
  }
}
